import MyMapsDialog, { IMyMapsDialog } from "./mymaps-dialog";
import { _ } from "~/i18n";
import { createMyMapsInput } from "../mymaps/input";
import { createMyMapsNote } from "../mymaps/note";
import { folderCopy } from "~/util/providers/mapybox";
import { getMapyboxError, mapyboxNotification } from "~/util/mapybox";
import { MAX_TITLE_LEN } from "./const";
import { checkNoteVersion } from "~/util/user-note";
import { TNoteVersion } from "~/interfaces";
import { openPropositionDialog } from "~/util/premium";
import { createMyMapsToggle } from "~/ui/mymaps/toggle";

interface IState {
	folderTitle: string;
	note: string;
	alwaysOnMap: boolean;
}

interface IMyMapsDuplicateFolder extends Pick<IMyMapsDialog, "onCancel" | "onClose"> {
	id: string;
	parentId: string;
	isPremiumUser?: boolean;
	noteVersion?: TNoteVersion;
	onDuplicate?: () => void;
	onError?: () => void;
	alwaysOnMap: boolean;
	title: string;
	description: string;
}

export default class MyMapsDuplicateFolder {
	_opts: IMyMapsDuplicateFolder;

	_dialog: MyMapsDialog;

	_state: IState;

	_origState: IState;

	_changed: boolean;

	_titleElem: HTMLInputElement;

	_noteElem: HTMLTextAreaElement;

	constructor(data: IMyMapsDuplicateFolder) {
		this._opts = {
			id: "",
			parentId: "",
			onDuplicate: () => {},
			onError: () => {},
			isPremiumUser: false,
			noteVersion: "version1",
			...data,
		};
		this._state = {
			folderTitle: data.title || "",
			note: data.description || "",
			alwaysOnMap: data.alwaysOnMap || false,
		};
		this._origState = {
			...this._state,
		};
		this._changed = false;
		this._titleElem = null;
		this._noteElem = null;

		this._build();
	}

	open() {
		this._dialog?.open();
		this._titleElem.focus();
	}

	destroy() {
		this._dialog?.destroy();
	}

	_closeDialog(force?: boolean) {
		this._dialog.close(force);
	}

	_build() {
		const dialog = new MyMapsDialog({
			titles: {
				main: [_("mymap.places.copyFolder")],
				saveBtn: _("mymap.contextMenu.duplicate"),
				cancelBtn: _("cancel"),
			},
			onThrowAwayTest: () => this._checkClose(),
			onThrowAwayConfirm: () => {
				this._closeDialog(true);
			},
			onSave: () => {
				this._onSave();
			},
			onCancel: () => {
				this._closeDialog(true);
			},
		});
		const contents = dialog.contents;
		const mainContent = contents[0];

		mainContent.classList.add("mymaps-dialog__defContent");

		// titulek
		const titleInput = createMyMapsInput({
			placeholder: _("mymap.folder.new"),
			onChange: folderTitle => {
				this._updateState({ folderTitle });
			},
			text: this._state.folderTitle,
			count: MAX_TITLE_LEN,
		});

		mainContent.append(titleInput.container);

		// poznamka
		if (this._opts.isPremiumUser || checkNoteVersion(this._opts.noteVersion, "version2")) {
			const noteData = createMyMapsNote({
				text: this._state.note,
				isPremiumUser: this._opts.isPremiumUser,
				noteVersion: this._opts.noteVersion,
				onChange: note => {
					this._updateState({ note });
				},
			});

			mainContent.append(noteData.container);

			this._noteElem = noteData.textarea;
		}

		// vzdy nad mapou
		if (Mapy.CUS.mapybox.alwaysOnMap) {
			mainContent.appendChild(createMyMapsToggle({
				text: _("mymap.folders.alwaysOnMap"),
				checked: this._state.alwaysOnMap || false,
				isPremiumUser: this._opts.isPremiumUser,
				onChange: alwaysOnMap => {
					this._updateState({ alwaysOnMap });
				},
				onClick: () => {
					if (!this._opts.isPremiumUser) {
						this._closeDialog(true);
						openPropositionDialog("MyMapsAlwaysOnMap");
					}
				},
			}));
		}

		this._titleElem = titleInput.input;
		this._dialog = dialog;
	}

	_updateState(newData: Partial<IState>) {
		Object.assign(this._state, newData);

		this._changed = this._state.folderTitle !== this._origState.folderTitle
			|| this._state.note !== this._origState.note;
	}

	_checkClose() {
		return !this._changed;
	}

	_sendError() {
		this._opts.onError();
		this._closeDialog(true);
	}

	async _onSave() {
		this._dialog.error = "";
		this._dialog.loading = true;

		try {
			const folderTitle = this._state.folderTitle || _("mymap.folder.new");
			const note = this._state.note.trim();
			const data = await folderCopy(this._opts.id, this._opts.parentId, {
				title: folderTitle,
				description: note,
				// eslint-disable-next-line camelcase
				always_on_map: this._state.alwaysOnMap,
			});

			mapyboxNotification({
				operation: "folder-copy",
				folderId: data.copied[0].new_id,
				folderTitle,
			});
			Mapy.getComponent("logger").log("my_maps_folder_duplicate", {
				isAlwaysOnMap: this._state.alwaysOnMap ? 1 : 0,
			});
		} catch (exc) {
			this._dialog.error = getMapyboxError({
				operation: "folder-copy",
			});
			this._dialog.loading = false;
			this._opts.onError();

			return;
		}

		this._opts.onDuplicate();
		this._closeDialog(true);
	}
}
